import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionHistory } from '@/hooks/useSessionHistory';
import { usePendingJobs } from '@/hooks/usePendingJobs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Clock, CheckCircle, Calendar, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface QuickSessionAccessProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickSessionAccess({ open, onOpenChange }: QuickSessionAccessProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { sessions = [], recentSessions = [] } = useSessionHistory();
  const { dlqJobs = [], recentlyCompleted = [] } = usePendingJobs();

  // Ensure arrays before filtering
  const sessionsArray = Array.isArray(sessions) ? sessions : [];
  const recentSessionsArray = Array.isArray(recentSessions) ? recentSessions : [];
  const dlqJobsArray = Array.isArray(dlqJobs) ? dlqJobs : [];
  const recentlyCompletedArray = Array.isArray(recentlyCompleted) ? recentlyCompleted : [];

  // Filter sessions based on search
  const filteredSessions = sessionsArray.filter(session =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    session.category_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Combine all items for quick access
  const quickAccessItems = [
    ...dlqJobsArray.map(job => ({
      id: job.id,
      type: 'dlq' as const,
      title: 'Pending meditation session',
      subtitle: `Created ${formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}`,
      icon: Clock,
      iconColor: 'text-orange-500',
    })),
    ...recentlyCompletedArray.map(job => ({
      id: job.id,
      type: 'completed' as const,
      title: 'Recently completed meditation',
      subtitle: `Completed ${formatDistanceToNow(new Date(job.completed_at!), { addSuffix: true })}`,
      icon: CheckCircle,
      iconColor: 'text-green-500',
    })),
    ...recentSessionsArray.slice(0, 5).map(session => ({
      id: session.id,
      type: 'session' as const,
      title: session.title,
      subtitle: `${session.category_id} • ${formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}`,
      icon: Calendar,
      iconColor: 'text-blue-500',
    })),
  ];

  const handleItemClick = (item: typeof quickAccessItems[0]) => {
    onOpenChange(false);

    if (item.type === 'session') {
      navigate(`/sessions/history?session=${item.id}`);
    } else {
      navigate(`/sessions/history?job=${item.id}`);
    }
  };

  // Reset search when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Quick Access</DialogTitle>
          <DialogDescription>
            Search and access your meditation sessions quickly
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          {/* Quick Access Items */}
          {!searchQuery && quickAccessItems.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Quick Access</h4>
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {quickAccessItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-accent transition-colors text-left"
                      >
                        <Icon className={`h-5 w-5 mt-0.5 ${item.iconColor}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{item.title}</p>
                          <p className="text-xs text-muted-foreground">{item.subtitle}</p>
                        </div>
                        {item.type === 'dlq' && (
                          <Badge variant="outline" className="text-xs">
                            Pending
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Search Results */}
          {searchQuery && (
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">
                Search Results ({filteredSessions.length})
              </h4>
              <ScrollArea className="h-[300px]">
                {filteredSessions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No sessions found matching "{searchQuery}"
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredSessions.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => {
                          onOpenChange(false);
                          navigate(`/sessions/history?session=${session.id}`);
                        }}
                        className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-accent transition-colors text-left"
                      >
                        <Calendar className="h-5 w-5 mt-0.5 text-blue-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{session.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {session.category_id} •{' '}
                            {formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}

          {/* Keyboard Shortcuts Hint */}
          <div className="pt-4 border-t">
            <p className="text-xs text-muted-foreground text-center">
              Press <kbd className="px-2 py-1 bg-muted rounded text-xs">Cmd+K</kbd> (Mac) or{' '}
              <kbd className="px-2 py-1 bg-muted rounded text-xs">Ctrl+K</kbd> (Windows) to quickly access this menu
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
